"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { useCompetitionHomeData } from "../../../context/CompetitionHomeDataContext";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { SeasonStatsCards } from "../../../partials/stats/SeasonStatsCards";
import { MatchCarousel } from "../../../partials/matches/MatchCarousel";
import { ArticleHero } from "../../../partials/articles/ArticleHero";
import { StandingsHorizontal } from "../../../partials/standings/StandingsHorizontal";
import { ArticlePair } from "../../../partials/articles/ArticlePair";
import { ArticleGrid } from "../../../partials/articles/ArticleGrid";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import { HOME_MATCHES, HOME_HERO, HOME_STANDINGS, HOME_ARTICLES, HOME_STATS } from "@/features/theme/sectionKeys";
import { AdSlot } from "@/features/ads/AdSlot";

/**
 * FootballCompetitionHomeContentStreamLayout - Matches Lead Carousel homepage layout.
 *
 * Layout sections (from top to bottom):
 * 0. Match Carousel - Horizontal scrollable match cards (past 12h to tomorrow)
 * 1. Hero Section - Full-width hero with featured article
 * 2. Video Shorts - Short-form video carousel (vertical)
 * 3. Horizontal Standings - Team logos bar with rank badges
 * 4. Featured Articles - Two side-by-side feature articles
 * 5. Promo slot 0 - Promotional content card
 * 6. News Grid - Three article cards row
 * 7. Video Highlights - Highlight video carousel (landscape)
 * 8. Promo slot 1 - Full-width promo banner
 * 9. Season Stats - Competition statistics (matches, goals, wins)
 */
interface FootballCompetitionHomeContentStreamLayoutProps {
  videoShortsSlot?: React.ReactNode;
  videoHighlightsSlot?: React.ReactNode;
  sectionStyles?: Record<string, BrandedStyle>;
}
export function FootballCompetitionHomeContentStreamLayout(t0) {
  const $ = _c(62);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles
  } = t1;
  const {
    latestNews,
    featuredPosts,
    promos,
    standings,
    homeSlices,
    competitionStats,
    goalDistribution,
    hasCompetitionStats
  } = useCompetitionHomeData();
  let t2;
  if ($[2] !== standings?.entries) {
    t2 = standings?.entries ?? [];
    $[2] = standings?.entries;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const standingsEntries = t2;
  const topArticles = featuredPosts?.length ? featuredPosts : latestNews;
  const heroArticle = topArticles[0];
  let t3;
  if ($[4] !== topArticles) {
    t3 = topArticles.slice(1, 3);
    $[4] = topArticles;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const featuredArticles = t3;
  let t4;
  if ($[6] !== latestNews) {
    t4 = latestNews.slice(3, 9);
    $[6] = latestNews;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const newsGridArticles = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <AdSlot id="content-top" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = sectionStyles?.[HOME_MATCHES];
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MatchCarousel />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6) {
    t8 = <SectionStrip brandedStyle={t6}>{t7}</SectionStrip>;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = sectionStyles?.[HOME_HERO];
  let t10;
  if ($[12] !== heroArticle) {
    t10 = heroArticle && <ArticleHero article={heroArticle} priority={true} />;
    $[12] = heroArticle;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t9) {
    t11 = <SectionStrip brandedStyle={t9}>{t10}</SectionStrip>;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = sectionStyles?.[HOME_STANDINGS];
  let t13;
  if ($[17] !== standingsEntries) {
    t13 = <StandingsHorizontal standings={standingsEntries} />;
    $[17] = standingsEntries;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <SectionStrip brandedStyle={t12}>{t13}</SectionStrip>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <AdSlot id="mid-scroll-1" />;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const t16 = sectionStyles?.[HOME_ARTICLES];
  let t17;
  if ($[23] !== featuredArticles) {
    t17 = <ArticlePair articles={featuredArticles} />;
    $[23] = featuredArticles;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t16 || $[26] !== t17) {
    t18 = <SectionStrip brandedStyle={t16}>{t17}</SectionStrip>;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  const t19 = promos[0] ?? null;
  let t20;
  if ($[28] !== t19) {
    t20 = <PromoSlot row={t19} />;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  const t21 = sectionStyles?.[HOME_ARTICLES];
  let t22;
  if ($[30] !== newsGridArticles) {
    t22 = <ArticleGrid articles={newsGridArticles} />;
    $[30] = newsGridArticles;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== t21 || $[33] !== t22) {
    t23 = <SectionStrip brandedStyle={t21}>{t22}</SectionStrip>;
    $[32] = t21;
    $[33] = t22;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <AdSlot id="mid-scroll-2" />;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  const t25 = promos[1] ?? null;
  let t26;
  if ($[36] !== t25) {
    t26 = <PromoSlot row={t25} />;
    $[36] = t25;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  const t27 = sectionStyles?.[HOME_STATS];
  let t28;
  if ($[38] !== competitionStats || $[39] !== goalDistribution || $[40] !== hasCompetitionStats || $[41] !== homeSlices) {
    t28 = hasCompetitionStats && <SeasonStatsCards competitionStats={competitionStats} goalDistribution={goalDistribution} teamWithMostWins={homeSlices.teamWithMostWins} />;
    $[38] = competitionStats;
    $[39] = goalDistribution;
    $[40] = hasCompetitionStats;
    $[41] = homeSlices;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  let t29;
  if ($[43] !== t27 || $[44] !== t28) {
    t29 = <SectionStrip brandedStyle={t27}>{t28}</SectionStrip>;
    $[43] = t27;
    $[44] = t28;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <AdSlot id="mid-scroll-3" />;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  const t31 = promos[2] ?? null;
  let t32;
  if ($[47] !== t31) {
    t32 = <PromoSlot row={t31} />;
    $[47] = t31;
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <AdSlot id="content-bottom" />;
    $[49] = t33;
  } else {
    t33 = $[49];
  }
  let t34;
  if ($[50] !== t11 || $[51] !== t14 || $[52] !== t18 || $[53] !== t20 || $[54] !== t23 || $[55] !== t26 || $[56] !== t29 || $[57] !== t32 || $[58] !== t8 || $[59] !== videoHighlightsSlot || $[60] !== videoShortsSlot) {
    t34 = <div className="min-h-screen bg-background"><main className="max-w-350 mx-auto px-4 py-6 space-y-10">{t5}{t8}{t11}{videoShortsSlot}{t14}{t15}{t18}{t20}{t23}{t24}{videoHighlightsSlot}{t26}{t29}{t30}{t32}{t33}</main></div>;
    $[50] = t11;
    $[51] = t14;
    $[52] = t18;
    $[53] = t20;
    $[54] = t23;
    $[55] = t26;
    $[56] = t29;
    $[57] = t32;
    $[58] = t8;
    $[59] = videoHighlightsSlot;
    $[60] = videoShortsSlot;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  return t34;
}